import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';

import UsersService from '~/services/UsersService';

export type EmployeeUser = {
  full_name: string;
  id_account: string;
  email: string;
  inline_roles?: Array<string>;
};

type Props = {
  user: App.User;
  isAdmin?: boolean;
  onUpdatePreferredAgent?: (agentId: string) => void;
  placeholder?: string;
  filterRoles?: Array<string>;
  brand?: string;
};

const MIN_SEARCH_LENGTH = 3;

export default function EmployeeUserList({
  user,
  isAdmin,
  onUpdatePreferredAgent,
  placeholder,
  filterRoles,
  brand,
}: Props) {
  const [options, setOptions] = useState<Array<EmployeeUser>>([]);
  const [loading, setLoading] = useState(false);
  const [resolvedAgent, setResolvedAgent] = useState<EmployeeUser | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<EmployeeUser | null>(null);

  const fetchOptions = useMemo(
    () =>
      debounce((input: string) => {
        if (input.length > 0 && input.length < MIN_SEARCH_LENGTH) {
          return;
        }
        setLoading(true);
        UsersService.getEmployeeUsers(input || undefined, 20, 0, filterRoles)
          .then((list: Array<EmployeeUser>) => setOptions(list))
          .finally(() => setLoading(false));
      }, 300),
    [filterRoles],
  );

  useEffect(() => {
    if (isAdmin) {
      setLoading(true);
      UsersService.getEmployeeUsers(undefined, 20, 0, filterRoles)
        .then((list: Array<EmployeeUser>) => setOptions(list))
        .finally(() => setLoading(false));
    }
    return () => {
      fetchOptions.clear();
    };
  }, [isAdmin, filterRoles, fetchOptions]);

  useEffect(() => {
    if (!user.preferred_agent_id || !brand) {
      setResolvedAgent(null);
      setSelectedValue(null);
      return;
    }

    UsersService.getUserSummary(user.preferred_agent_id, brand)
      .then((summary: { id_member?: string; full_name?: string; email?: string }) => {
        const agent: EmployeeUser = {
          id_account: summary.id_member ?? user.preferred_agent_id,
          full_name: summary.full_name ?? '',
          email: summary.email ?? '',
        };
        setResolvedAgent(agent);
        setSelectedValue(agent);
      })
      .catch(() => {
        setResolvedAgent(null);
        setSelectedValue(null);
      });
  }, [user.preferred_agent_id, brand]);

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, value: string, reason: string) => {
      setInputValue(value);
      if (reason === 'input' || reason === 'clear') {
        fetchOptions(value);
      }
    },
    [fetchOptions],
  );

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: EmployeeUser | null) => {
      setSelectedValue(newValue);
      if (newValue) {
        onUpdatePreferredAgent?.(newValue.id_account);
      }
    },
    [onUpdatePreferredAgent],
  );

  if (!isAdmin) {
    return <>{resolvedAgent?.full_name ?? ''}</>;
  }

  const noOptionsText =
    inputValue.length > 0 && inputValue.length < MIN_SEARCH_LENGTH
      ? `Type at least ${MIN_SEARCH_LENGTH} characters`
      : 'No results';

  return (
    <Autocomplete
      options={options}
      value={selectedValue}
      inputValue={inputValue}
      loading={loading}
      getOptionLabel={(opt) => (opt.email ? `${opt.full_name} (${opt.email})` : opt.full_name)}
      isOptionEqualToValue={(opt, val) => opt.id_account === val.id_account}
      filterOptions={(x) => x}
      onInputChange={handleInputChange}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, opt) => (
        <li {...props} key={opt.id_account}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">{opt.full_name}</Typography>
            {opt.email && <Typography variant="body2">({opt.email})</Typography>}
          </Stack>
        </li>
      )}
      noOptionsText={noOptionsText}
      fullWidth
    />
  );
}
